<template>
    <vx-modal v-model:visible="visible" padding="standard" size="lg">
        <template v-slot:header>Custom Attributes</template>
        <div>
            <div class="flex flex-col gap-2 mb-3 lg:flex-row lg:mb-6">
                <div class="lg:w-60">
                    <vx-label>Key</vx-label>
                    <vx-selectable
                        size="lg"
                        :options="options"
                        :loading="searching"
                        :taggable="true"
                        @search="search"
                        v-model="key"
                        @option:selected="focusValue()"
                        :error="displayFirstError(validate.key)"
                    >
                        <template v-slot:no-options> Search or create a custom attribute key. </template>
                    </vx-selectable>
                </div>
                <div class="flex grow gap-2">
                    <div class="grow">
                        <vx-label>Value</vx-label>
                        <vx-input
                            ref="valueEl"
                            size="lg"
                            v-model="value"
                            @keypress.enter="add()"
                            :error="displayFirstError(validate.value)"
                        ></vx-input>
                    </div>
                    <div class="pt-[25px]">
                        <vx-button
                            @click="add()"
                            :loading="adding"
                            data-test="custom-attribute-add"
                            size="lg"
                            type="button"
                        >
                            <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                        </vx-button>
                    </div>
                </div>
            </div>

            <vx-alert v-if="error" color="danger" class="mb-3" :closeable="false">{{ error }}</vx-alert>
            <vx-icon-loading v-if="loadingCustomAttributes && !customAttributes.length" spin></vx-icon-loading>
            <vx-alert :closeable="false" v-else-if="!customAttributes.length">
                No custom attributes were added to this contact.
            </vx-alert>
            <div v-else>
                <vx-list-animated v-model="customAttributes" :removeable="false" @remove="remove">
                    <template v-slot:item="{ item }">
                        <div
                            class="grid grid-cols-1 w-full gap-1.5 px-5 py-3 text-xs text-slate-900 lg:grid-cols-2 lg:gap-2"
                        >
                            <div class="line-clamp-3 break-all font-bold lg:font-normal">{{ item.key }}</div>
                            <div class="line-clamp-3 break-all">{{ item.value }}</div>
                        </div>
                    </template>
                </vx-list-animated>
            </div>
        </div>
    </vx-modal>
</template>

<script setup>
import { customAttributesClient } from '~/services';
import debounce from '~/utils/debounce';
import {
    VxInput,
    VxLabel,
    VxAlert,
    VxButton,
    VxIconLoading,
    VxListAnimated,
    VxModal,
    VxSelectable,
} from '@voxie/frontend-components';
import { ref, nextTick, watch } from 'vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import useVuelidate from '@vuelidate/core';
import { maxLength, requiredIf } from '@vuelidate/validators';
import { csvInjection, customAttributeKey, customAttributeValue, displayFirstError } from '~/utils/validation';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { catchRequestError } from '~/utils/request';

const teamId = Spark.state.currentTeam.id;

const visible = defineModel('visible', {
    default: false,
});

const { customAttributes, getCustomAttributes, loadingCustomAttributes, upsertCustomAttribute, deleteCustomAttribute } =
    useContactDetails();

watch(
    visible,
    () => {
        if (visible.value && !customAttributes.value.length) {
            getCustomAttributes();
        }
    },
    {
        immediate: true,
    }
);

const valueEl = ref();
const focusValue = async () => {
    await nextTick();
    valueEl.value.field?.focus();
};

const error = ref('');
const key = ref('');
const value = ref('');
const options = ref([]);
const searching = ref(false);
const adding = ref(false);

const search = debounce((search) => {
    searching.value = true;

    customAttributesClient
        .customAttributesAutocomplete(teamId, {
            query: search,
        })
        .then((response) => {
            options.value = response.data.data;
            searching.value = false;
        });
}, 200);

const validate = useVuelidate(
    {
        key: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(80),
            format: customAttributeKey,
            csvInjection: csvInjection,
        },
        value: {
            requiredIf: requiredIf(adding),
            maxLength: maxLength(350),
            format: customAttributeValue,
            csvInjection: csvInjection,
        },
    },
    {
        key,
        value,
    }
);

const add = async () => {
    if (adding.value) {
        return;
    }
    adding.value = true;
    await nextTick();

    const isValid = await validate.value.$validate();
    if (!isValid) {
        setTimeout(() => {
            adding.value = false;
        }, 3500);
        return;
    }

    try {
        await upsertCustomAttribute({
            key: key.value,
            value: value.value,
        });

        key.value = '';
        value.value = '';
        adding.value = false;
    } catch (e) {
        error.value = catchRequestError(e);
    }
};

const remove = async (customAttribute) => {
    try {
        await deleteCustomAttribute(customAttribute);
    } catch (e) {
        error.value = catchRequestError(e);
    }
};
</script>
